import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, Briefcase, AlertTriangle, Check, ChevronRight, LogOut, Loader2, Plus, ClipboardList, Calendar, MapPin, Home, Settings, History } from 'lucide-react';
import axios from 'axios';
import { enqueuePunch, isNetworkError } from '../utils/offlineQueue';
import OfflineSyncBanner from '../components/OfflineSyncBanner';
import BreakTracker from '../components/timesheet/BreakTracker';
import NeedsReview from '../components/timesheet/NeedsReview';
import { ReportMissingTimeButton } from '../components/timesheet/ReportMissingTime';
import WorkerSummary from '../components/timesheet/WorkerSummary';
import ClockOutConfirm from '../components/timesheet/ClockOutConfirm';
import MealBreakDialog from '../components/MealBreakDialog';
import NotificationBell from '../components/NotificationBell';
import ClockOutReceipt from '../components/ClockOutReceipt';
import EmptyState from '../components/EmptyState';

const API = `${window.__BACKEND_URL__}/api`;

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, loading: authLoading } = useAuth();
  const [clockStatus, setClockStatus] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingOut, setClockingOut] = useState(false);
  const [showClockOut, setShowClockOut] = useState(false);
  const [mealPrompt, setMealPrompt] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [elapsed, setElapsed] = useState('0h 0m');

  useEffect(() => { if (token) fetchAll(); }, [token]);

  useEffect(() => {
    if (!clockStatus?.clocked_in) return;
    const timer = setInterval(() => {
      // Defense-in-depth: the backend now emits ISO timestamps with the
      // `Z` UTC suffix (Feb 2026 fix), so `new Date()` parses them as
      // UTC. If a legacy server response still ships a naive ISO without
      // an offset, we re-tag it as UTC before computing elapsed time so
      // the worker dashboard never shows a wrong "Hours worked" counter.
      let iso = clockStatus.clock_in_time;
      if (typeof iso === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test(iso)) {
        iso = `${iso}Z`;
      }
      const start = new Date(iso);
      const diff = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
      setElapsed(`${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`);
    }, 1000);
    return () => clearInterval(timer);
  }, [clockStatus]);

  const fetchAll = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statusRes, annRes, jobsRes] = await Promise.all([
        axios.get(`${API}/clock-in/status`, { headers }),
        axios.get(`${API}/safety-announcements`, { headers }),
        axios.get(`${API}/jobs`, { headers })
      ]);
      setClockStatus(statusRes.data);
      setAnnouncements(annRes.data);
      setJobs(jobsRes.data.filter(j => j.status !== 'completed' && j.status !== 'archived'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAcknowledge = async (annId) => {
    try {
      await axios.post(`${API}/safety-announcements/${annId}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(prev => prev.map(a => a.id === annId ? { ...a, acknowledged: true } : a));
    } catch (err) {
      console.error('Acknowledge safety announcement failed', err);
      // Roll back the optimistic UI update so the user sees they must retry.
      setAnnouncements(prev => prev.map(a => a.id === annId ? { ...a, acknowledged: false } : a));
    }
  };

  const finishAndLeave = () => { logout(); navigate('/'); };

  const handleClockOut = async (deductBreak = false, confirmNoBreak = false) => {
    setClockingOut(true);
    try {
      const res = await axios.post(
        `${API}/clock-out/quick`,
        { deduct_break: deductBreak, confirm_no_break: confirmNoBreak },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Alberta ESA: 5h+ shift without a logged break — server holds the
      // clock-out until the worker answers. NEVER treat this as success.
      if (res.data?.meal_break_warning) {
        setMealPrompt({ shift_hours: res.data.shift_hours, threshold: res.data.break_threshold_hours });
        setShowClockOut(false);
        return;
      }
      setMealPrompt(null);
      if (res.data.jobs_still_in_progress > 0) {
        alert(`Warning: You still have ${res.data.jobs_still_in_progress} job(s) in progress.`);
      }
      setReceipt({ hours: res.data.hours_worked, deducted: res.data.meal_break_deducted });
    } catch (err) {
      if (isNetworkError(err)) {
        enqueuePunch('clock_out', {});
        alert('No signal — your clock-out was saved on this device and will sync automatically once you\'re back online.');
        finishAndLeave();
      } else { console.error(err); }
    }
    finally { setClockingOut(false); setShowClockOut(false); }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1E88E5' }} /></div>;
  }

  const unacked = announcements.filter(a => !a.acknowledged);
  const activeJobs = jobs.filter(j => ['pending', 'todo', 'accepted', 'in_progress', 'paused'].includes(j.status));
  const nextJob = activeJobs[0];
  const clockInTime = clockStatus?.clocked_in ? new Date(clockStatus.clock_in_time).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <div className="min-h-screen" style={{ background: '#0B1321' }} data-testid="staff-dashboard">
      <OfflineSyncBanner />
      <MealBreakDialog
        open={!!mealPrompt}
        shiftHours={mealPrompt?.shift_hours}
        thresholdHours={mealPrompt?.threshold}
        busy={clockingOut}
        onDeduct={() => handleClockOut(true, false)}
        onNoBreak={() => handleClockOut(false, true)}
        onCancel={() => setMealPrompt(null)}
      />
      <ClockOutReceipt receipt={receipt} onClose={finishAndLeave} autoCloseMs={5000} />
      <div className="max-w-lg mx-auto px-4 pt-4 pb-24">
        {/* ===== TOP HEADER (matches mobile ref) ===== */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#1E88E5' }}>
              <span className="text-white font-bold text-xs">H</span>
            </div>
            <span className="text-white font-bold text-sm">HINES <span style={{ color: '#1E88E5' }}>TIME</span></span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </div>

        {/* ===== GREETING (matches "Hi Mark!") ===== */}
        <div className="mb-5">
          <h1 className="text-3xl font-bold text-white" data-testid="staff-greeting">
            Hi {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}, {new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* ===== CLOCK IN/OUT CARD (blue gradient, matches ref) ===== */}
        <button
          onClick={clockStatus?.clocked_in ? () => setShowClockOut(true) : () => navigate('/clock-in')}
          className="w-full p-4 rounded-2xl mb-4 flex items-center justify-between transition-all active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #1E88E5, #42A5F5)' }}
          data-testid="clock-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Clock In / Out</p>
              <p className="text-white/70 text-xs">
                {clockStatus?.clocked_in ? `Clocked In ${clockInTime}` : 'Not Clocked In'}
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/60" />
        </button>

        {/* ===== BREAK TRACKER (visible while clocked in) ===== */}
        {clockStatus?.clocked_in && (
          <BreakTracker token={token} clockedIn={true} />
        )}

        {/* ===== TODAY / THIS WEEK / MY TIMESHEETS ===== */}
        <WorkerSummary token={token} clockStatus={clockStatus} />

        <ClockOutConfirm
          token={token}
          open={showClockOut}
          clockStatus={clockStatus}
          elapsed={elapsed}
          busy={clockingOut}
          onConfirm={() => handleClockOut()}
          onQrDone={() => { setShowClockOut(false); logout(); navigate('/'); }}
          onClose={() => setShowClockOut(false)}
        />

        {/* ===== MY SCHEDULE CARD (matches ref) ===== */}
        <button
          onClick={() => navigate('/jobs')}
          className="w-full ht-card p-4 mb-4 flex items-center justify-between hover:border-blue-500/30 transition-all"
          data-testid="schedule-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.1)' }}>
              <Calendar className="w-5 h-5" style={{ color: '#1E88E5' }} />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">My Schedule</p>
              <p className="text-xs" style={{ color: '#64748B' }}>
                {nextJob ? `Upcoming: ${nextJob.title}` : 'No upcoming jobs'}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: '#64748B' }} />
        </button>

        {/* ===== START NEW JOB + LOG TASKS (two square buttons, matches ref) ===== */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button onClick={() => navigate('/jobs')} className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-blue-500/30 transition-all" data-testid="start-new-job">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.12)' }}>
              <Plus className="w-5 h-5" style={{ color: '#00C896' }} />
            </div>
            <span className="text-sm font-medium text-white">Start New Job</span>
          </button>
          <button onClick={() => navigate('/reports')} className="ht-card p-4 flex flex-col items-center gap-2 text-center hover:border-blue-500/30 transition-all" data-testid="log-tasks">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.12)' }}>
              <ClipboardList className="w-5 h-5" style={{ color: '#1E88E5' }} />
            </div>
            <span className="text-sm font-medium text-white">Log Tasks</span>
          </button>
        </div>

        {/* ===== TIME HISTORY + REPORT MISSING TIME ===== */}
        <button
          onClick={() => navigate('/my-time')}
          className="w-full ht-card p-4 mb-3 flex items-center justify-between hover:border-blue-500/30 transition-all"
          data-testid="my-time-history-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.1)' }}>
              <History className="w-5 h-5" style={{ color: '#1E88E5' }} />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">My Time History</p>
              <p className="text-xs" style={{ color: '#64748B' }}>Shift statuses, returns & corrections</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: '#64748B' }} />
        </button>
        <div className="mb-4">
          <ReportMissingTimeButton token={token} />
        </div>

        {/* ===== SHIFTS NEEDING REVIEW & CERTIFICATION ===== */}
        <NeedsReview token={token} />

        {/* ===== SAFETY ANNOUNCEMENTS ===== */}
        {unacked.length > 0 && (
          <div className="mb-4" data-testid="safety-section">
            {unacked.map((ann) => (
              <div key={ann.id} className="ht-card p-4 mb-2" style={ann.priority === 'critical' ? { borderColor: '#EF4444' } : ann.priority === 'warning' ? { borderColor: '#FFA000' } : {}}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ann.priority === 'critical' ? '#EF4444' : '#FFA000' }} />
                    <div>
                      <p className="text-sm font-semibold text-white">{ann.title}</p>
                      <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>{ann.body}</p>
                    </div>
                  </div>
                  <button onClick={() => handleAcknowledge(ann.id)} className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.15)' }} data-testid={`ack-${ann.id}`}>
                    <Check className="w-4 h-4" style={{ color: '#00C896' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== JOB CARDS (matches ref with map placeholder + details) ===== */}
        {activeJobs.map((job) => (
          <button key={job.id} onClick={() => navigate('/jobs')} className="w-full ht-card p-4 py-3 mb-3 min-h-[56px] text-left hover:border-blue-500/30 transition-all" data-testid={`job-card-${job.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-white truncate">{job.title}</p>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#64748B' }} />
                </div>
                {job.customer_name && (
                  <p className="text-xs mb-1" style={{ color: '#A0AEC0' }}>
                    <MapPin className="w-3 h-3 inline mr-1" />{job.customer_name}
                    {job.city && `, ${job.city}`}
                  </p>
                )}
                {job.description && (
                  <p className="text-xs" style={{ color: '#64748B' }}>{job.description?.substring(0, 60)}</p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <JobPill status={job.status} />
                  {clockStatus?.clocked_in && (
                    <span className="text-xs" style={{ color: '#64748B' }}>{clockInTime}</span>
                  )}
                </div>
              </div>
              {/* Map placeholder matching ref */}
              <div className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: '#1F2D3F' }}>
                <MapPin className="w-6 h-6" style={{ color: '#1E88E5' }} />
              </div>
            </div>
          </button>
        ))}

        {activeJobs.length === 0 && (
          <div className="ht-card" data-testid="staff-no-jobs-empty-state">
            <EmptyState
              icon={Briefcase}
              title="No jobs assigned"
              description="Your supervisor will assign jobs to you here."
            />
          </div>
        )}
      </div>

      {/* ===== BOTTOM NAV (matches ref: 5 icons) ===== */}
      <nav className="fixed bottom-0 left-0 right-0 z-50" style={{ background: 'rgba(11,19,33,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: '1px solid #2D3748' }} data-testid="staff-bottom-nav">
        <div className="grid grid-cols-5 max-w-lg mx-auto py-2">
          <NavIcon icon={Home} label="Home" active />
          <NavIcon icon={Briefcase} label="Jobs" onClick={() => navigate('/jobs')} />
          <NavIcon icon={Calendar} label="Schedule" onClick={() => navigate('/jobs')} />
          <NavIcon icon={ClipboardList} label="Tasks" onClick={() => navigate('/reports')} />
          <NavIcon icon={Settings} label="Settings" onClick={() => navigate('/settings')} />
        </div>
      </nav>
    </div>
  );
}

function JobPill({ status }) {
  const map = {
    pending: { bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0', label: 'Pending' },
    todo: { bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0', label: 'To Do' },
    accepted: { bg: 'rgba(30,136,229,0.15)', color: '#1E88E5', label: 'Accepted' },
    in_progress: { bg: 'rgba(30,136,229,0.15)', color: '#1E88E5', label: 'Active' },
    paused: { bg: 'rgba(255,160,0,0.15)', color: '#FFA000', label: 'Paused' },
  };
  const s = map[status] || map.pending;
  return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}

function NavIcon({ icon: Icon, label, active, onClick, danger }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center py-1">
      <Icon className="w-5 h-5 mb-0.5" strokeWidth={active ? 2.5 : 1.5} style={{ color: danger ? '#EF4444' : active ? '#1E88E5' : '#4A5568' }} />
      <span className="text-[10px] font-medium" style={{ color: active ? '#1E88E5' : '#4A5568' }}>{label}</span>
    </button>
  );
}
